import React, {
  Fragment,
  useEffect,
  useContext,
  useState,
  useMemo,
  useRef,
} from "react";
import { UserContext } from "../../../../../../context/UserContext";
import { FlowHandlersContext } from "../../../../Menu/FlowContainer";
import { getCurrency } from "../../../../../../legacyComponents/util/formating";
import { options } from "../util";
import {
  Stack,
  Typography,
  Box,
  Link,
  InputAdornment,
  OutlinedInput,
} from "@mui/material";
import { styled } from "@mui/system";
import { FormContainer, NavButtons } from "../../../../Menu/Flow";
import { AgeAllowanceTable, FamilyAllowanceTable } from "../components";
import CheckboxField from "../../../../../../components/CheckboxField";
import { TextField } from "../../../../../../legacyComponents/componentsV2/FlowV2/Fields";
import { Field } from "formik";
import employerFlowStyles from "../../styles";
import styles from "./styles";
import { checkAllowanceAmounts } from "../util/allowanceChecks";

const getFamilyAllowanceChoice = (dependentPercent) => {
  if (
    dependentPercent === null ||
    dependentPercent === undefined ||
    dependentPercent === ""
  ) {
    return "";
  }
  return Number(dependentPercent) > 0 ? "YES" : "NO";
};
/**
 * The selected option of the "Apply ACA affordability minimum?" radio, stored as
 * metalOmitAcaAffordability: "true" = omit the minimum (No), "false" = apply it (Yes),
 * undefined = nothing selected.
 */
const getOmitAcaSelection = (values) =>
  Object.keys(values?.metalOmitAcaAffordability || {}).find(
    (key) => values.metalOmitAcaAffordability[key],
  );
const LearnMoreButton = styled(Link)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "14px 20px",
  borderRadius: "14px",
  border: "1px solid #6C63FF",
  backgroundColor: "rgba(108, 99, 255, .1)",
  transition: "all .2s ease",
  color: "#6C63FF",
  fontWeight: 500,
  fontSize: "14px",
  textDecoration: "none",
  width: "fit-content",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.8,
    textDecoration: "none",
  },
});

const LearnMoreLink = styled(Link)({
  color: "#6C63FF",
  fontWeight: 500,
  fontSize: "14px",
  textDecoration: "none",
  whiteSpace: "nowrap",
  cursor: "pointer",
  "&:hover": { textDecoration: "underline" },
});

/**
 * Sits between the amount inputs and the age tables — the tables are the
 * detail, this is the rule that governs whatever was entered above.
 */
function AccrualNote({ href }) {
  return (
    <Typography
      sx={{
        fontSize: "14px",
        color: "#465578",
        lineHeight: 1.6,
      }}
    >
      The monthly allowance accrues throughout the plan year, and does not carry
      over from one plan year to the next.{" "}
      {href && (
        <LearnMoreLink href={href} target="_blank" rel="noopener noreferrer">
          Learn More →
        </LearnMoreLink>
      )}
    </Typography>
  );
}

/** One error line, in the page's error style. */
function ErrorLine({ children }) {
  return <div className="error-message-fields">{children}</div>;
}

function buildTouchedFromErrors(errors) {
  if (!errors || typeof errors !== "object") return true;
  if (Array.isArray(errors))
    return errors.map((item) => buildTouchedFromErrors(item));
  return Object.keys(errors).reduce((acc, key) => {
    acc[key] = buildTouchedFromErrors(errors[key]);
    return acc;
  }, {});
}

export default function AllowanceType(props) {
  const {
    mode,
    exitUrl,
    step,
    formik,
    displayOnly,
    permissibleClassId,
    systemVariables,
  } = props;
  const { getEmployerPlan, getPermissibleClass } = useContext(UserContext);
  const flowHandlers = useContext(FlowHandlersContext);
  const {
    errors,
    touched,
    values,
    setFieldValue,
    setFieldTouched,
    setValues,
    submitCount,
  } = formik;

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [topLevelChoice, setTopLevelChoice] = useState(() => {
    const methods = values?.allowanceMethod || {};
    if (methods.MANUAL) return "SELF";
    if (methods.ACA_MINIMUM || methods.PCT_OF_METAL) return "CALCULATED";
    return "";
  });

  // ── Employer/Plan context ──
  let employerPlan = null;
  let permissibleClass = null;
  if (permissibleClassId) {
    permissibleClass = getPermissibleClass(permissibleClassId);
    const employerPlanId = permissibleClass?.employerPlanId;
    employerPlan = getEmployerPlan(employerPlanId);
  }
  const planType = employerPlan?.type || "";

  const v2 = permissibleClass ? permissibleClass?.v2 : true;
  // Legacy PCT_OF_METAL classes (metalV2 off) price dependents from their own rates,
  // so they don't get the spouse/dependent percentage. New classes use metalV2.
  const metalV2 = permissibleClass ? permissibleClass.metalV2 === true : true;

  const shouldShowErrors = hasAttemptedSubmit || submitCount > 0;

  /**
   * EVERY AMOUNT RULE, worked out fresh from the current values on each
   * render (allowanceChecks.js). Nothing is written back to the form: the
   * red cells, the messages and the save check all read this one answer,
   * so they can't disagree or go stale.
   */
  const amountCheck = useMemo(() => checkAllowanceAmounts(values), [values]);

  /**
   * THE SAVE CHECK, as a Formik field-level rule. NavButtons' Save & Exit
   * and handleNext both call formik.validateForm(), which runs every
   * registered field rule, so an amount error stops both without either
   * needing to know about it.
   *
   * The rule reads the latest values through a ref: Formik hands a field
   * rule only that field's own value, and this check needs all of them.
   */
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const validateAmounts = () => {
    const result = checkAllowanceAmounts(valuesRef.current);
    if (result.valid) return undefined;
    return result.ageMessage || Object.values(result.fieldErrors)[0];
  };
  // Shown once the user has tried to move on, or Save & Exit marked it touched.
  const showAmountErrors =
    shouldShowErrors || Boolean(touched?.allowanceAmounts);

  /**
   * The age tables read values.columnErrors for their red cells. They get
   * the computed answer through their formik prop, so the tables need no
   * change and nothing is stored in the form.
   */
  const tableProps = useMemo(
    () => ({
      ...props,
      formik: {
        ...formik,
        values: { ...values, columnErrors: amountCheck.cellErrors },
      },
    }),
    [props, formik, values, amountCheck.cellErrors],
  );

  let selfLabel = "Self Amount";
  if (values?.maxBenefit?.SELF) {
    selfLabel = "Monthly Amount";
  }

  let annualFamilyAmountMax = 0;
  let monthlyFamilyDisplayMax = "";
  const inputOptions = options.maxBenefits?.[planType] || [];

  const [familyAllowanceChoice, setFamilyAllowanceChoice] = useState(() =>
    getFamilyAllowanceChoice(values?.metalPercentDependent),
  );

  useEffect(() => {
    setFamilyAllowanceChoice(
      getFamilyAllowanceChoice(formik.initialValues?.metalPercentDependent),
    );
  }, [permissibleClassId, formik.initialValues]);

  if (planType === "QSEHRA") {
    annualFamilyAmountMax =
      systemVariables?.MAX_CONTRIBUTION_ANNUAL?.family || 0;
  }
  if (annualFamilyAmountMax > 0) {
    monthlyFamilyDisplayMax = getCurrency({
      amount: Math.floor(annualFamilyAmountMax / 12.0),
      actions: ["divide"],
    });
  }

  // ── Top-level selection options ──
  const topLevelOptions = [
    { display: "I set the allowance", value: "SELF" },
    { display: "Salusion calculates the allowance", value: "CALCULATED" },
  ];

  const calculationMethodOptions = [
    {
      display: "Percentage of insurance cost",
      value: "PCT_OF_METAL",
      bullets: [
        "Salusion calculates allowances based on a selected percentage of the average Bronze, Silver, or Gold found on the marketplace determined by age and site of employment.",
      ],
      links: [
        {
          text: "Learn how allowances are calculated →",
          href: "https://salusion.com/learning-center/setting-the-allowance/#percentage-of-insurance-cost-ichra-only",
        },
        {
          text: "Calculate allowances based on your company's census →",
          href: "https://my.salusion.com/calculator/metal-based-ichra-employer",
        },
      ],
    },
    {
      display: "Lowest cost ACA affordable",
      value: "ACA_MINIMUM",
      bullets: [
        "Salusion calculates the minimum allowance required to satisfy ACA affordability rules for Applicable Large Employers using the federal poverty level and site-of-employment safe harbors.",
      ],
      links: [
        {
          text: "Learn how allowances are calculated →",
          href: "https://salusion.com/learning-center/setting-the-allowance/#lowest-cost-aca-affordable-ichra-only",
        },
        {
          text: "Calculate allowances based on your company's census →",
          href: "https://my.salusion.com/calculator/affordable-ichra-employer",
        },
      ],
    },
  ];

  // Derive which method is active
  const activeMethod = (() => {
    const methods = values?.allowanceMethod || {};
    if (methods.MANUAL) return "MANUAL";
    if (methods.PCT_OF_METAL) return "PCT_OF_METAL";
    if (methods.ACA_MINIMUM) return "ACA_MINIMUM";
    return null;
  })();

  const hasSelection = (() => {
    if (topLevelChoice === "SELF") return true;
    if (topLevelChoice === "CALCULATED") {
      const methods = values?.allowanceMethod || {};
      return methods.ACA_MINIMUM || methods.PCT_OF_METAL;
    }
    return false;
  })();

  const showSelectionError = shouldShowErrors && !hasSelection;

  // ── Effects: top-level sync ──
  useEffect(() => {
    if (topLevelChoice === "SELF") {
      setFieldValue("allowanceMethod", { MANUAL: true });
    } else if (topLevelChoice === "CALCULATED") {
      const methods = values?.allowanceMethod || {};
      if (methods.MANUAL) {
        const { MANUAL, ...rest } = methods;
        setFieldValue("allowanceMethod", rest);
      }
    }
  }, [topLevelChoice]);

  useEffect(() => {
    const nextRequiredValue = hasSelection ? "yes" : "";
    if ((values?.allowanceMethod_required || "") !== nextRequiredValue) {
      setFieldValue("allowanceMethod_required", nextRequiredValue);
    }
  }, [hasSelection, values?.allowanceMethod_required, setFieldValue]);

  useEffect(() => {
    if (shouldShowErrors && errors?.allowanceMethod) {
      formik.setFieldTouched("allowanceMethod", true, false);
    }
  }, [shouldShowErrors, errors?.allowanceMethod]);

  useEffect(() => {
    if (shouldShowErrors && errors?.allowanceMethod_required) {
      formik.setFieldTouched("allowanceMethod_required", true, false);
    }
  }, [shouldShowErrors, errors?.allowanceMethod_required]);

  // ── Effects: benefit-type selection ──
  useEffect(() => {
    const maxBenefit = values?.maxBenefit || {};
    const hasBenefitSelection = Object.values(maxBenefit).some(Boolean);
    const nextRequiredValue = hasBenefitSelection ? "yes" : "";
    if ((values?.maxBenefit_required || "") !== nextRequiredValue) {
      setFieldValue("maxBenefit_required", nextRequiredValue);
    }
  }, [values?.maxBenefit, values?.maxBenefit_required, setFieldValue]);

  useEffect(() => {
    if (shouldShowErrors) {
      if (errors?.maxBenefit) setFieldTouched("maxBenefit", true, false);
      if (errors?.metalPercent) setFieldTouched("metalPercent", true, false);
    }
  }, [
    shouldShowErrors,
    errors?.maxBenefit,
    errors?.metalPercent,
    setFieldTouched,
  ]);

  // ── Handlers ──
  function handleTopLevelChange(newValues) {
    if (newValues.SELF) {
      if (topLevelChoice === "CALCULATED") {
        setFieldValue("allowanceMethod", { MANUAL: true });
      }
      setTopLevelChoice("SELF");
    } else if (newValues.CALCULATED) {
      if (topLevelChoice === "SELF") {
        setFieldValue("maxBenefit", {});
        setFieldValue("maxBenefit_required", "");
      }
      setTopLevelChoice("CALCULATED");
    }
  }

  /**
   * Next. The amount rules are in formik.validateForm() (the field rule
   * above), the same check Save & Exit runs. The metal checks below are
   * Next-only, as before.
   */
  async function handleNext(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setHasAttemptedSubmit(true);

    const validationErrors = await formik.validateForm();
    const hasErrors = Object.keys(validationErrors).length > 0;

    // Additional validation for PCT_OF_METAL
    let hasMetalErrors = false;
    if (activeMethod === "PCT_OF_METAL") {
      if (!values.metalPercent || values.metalPercent <= 0) {
        hasMetalErrors = true;
        formik.setFieldTouched("metalPercent", true, false);
      }
      if (!values.metalLevel) {
        hasMetalErrors = true;
        formik.setFieldTouched("metalLevel", true, false);
      }
      // Spouse and dependents (metalV2 only): a choice is required, and Yes needs a percentage.
      if (metalV2) {
        if (!familyAllowanceChoice) hasMetalErrors = true;
        if (
          familyAllowanceChoice === "YES" &&
          (!values.metalPercentDependent ||
            Number(values.metalPercentDependent) <= 0)
        ) {
          hasMetalErrors = true;
          formik.setFieldTouched("metalPercentDependent", true, false);
        }
      }
      // "Apply ACA affordability minimum?" must be answered.
      if (getOmitAcaSelection(values) === undefined) {
        hasMetalErrors = true;
        formik.setFieldTouched("metalOmitAcaAffordability", true, false);
      }
    }

    if (hasErrors || !hasSelection || hasMetalErrors) {
      formik.setTouched(buildTouchedFromErrors(validationErrors), true);
      return false;
    }

    await formik.submitForm();

    if (flowHandlers?.onNext) {
      await flowHandlers.onNext(formik.values);
    }
    return false;
  }

  // Synthetic formik for top-level radio
  const topLevelFormik = {
    ...formik,
    values: {
      ...values,
      topLevelChoice: topLevelChoice ? { [topLevelChoice]: true } : {},
    },
    setFieldValue: (name, value) => {
      if (name === "topLevelChoice") handleTopLevelChange(value);
    },
  };

  // ── Learn More link ──
  const learnMoreHref = (() => {
    const maxBenefit = values?.maxBenefit || {};
    const hasBenefitSelection = Object.values(maxBenefit).some(Boolean);
    if (!hasBenefitSelection) return null;
    if (maxBenefit.FAMILY_AGE)
      return "https://salusion.com/learning-center/setting-the-allowance/#by-age-family-size";
    if (maxBenefit.AGE)
      return "https://salusion.com/learning-center/setting-the-allowance/#by-age";
    if (maxBenefit.FAMILY)
      return "https://salusion.com/learning-center/setting-the-allowance/#by-family-size";
    if (
      planType === "ICHRA"
        ? !maxBenefit.FAMILY && !maxBenefit.FAMILY_AGE && !maxBenefit.AGE
        : maxBenefit.SELF
    )
      return "https://salusion.com/learning-center/setting-the-allowance/#one-size-fits-all";
    return null;
  })();

  // ── Shared pieces ──

  /** The registered save rule. Renders nothing visible. */
  const amountsRule = (
    <Field name="allowanceAmounts" validate={validateAmounts}>
      {({ field }) => (
        <input
          type="hidden"
          name={field.name}
          value={field.value ?? ""}
          readOnly
        />
      )}
    </Field>
  );

  /** The age-table message, from the live check. */
  const ageTableMessage =
    showAmountErrors && amountCheck.ageMessage ? (
      <ErrorLine>{amountCheck.ageMessage}</ErrorLine>
    ) : null;

  /** A monthly amount's $5,000 message, shown as soon as it's over. */
  const monthlyMaxMessage = (field) =>
    amountCheck.fieldErrors[field] ? (
      <ErrorLine>{amountCheck.fieldErrors[field]}</ErrorLine>
    ) : null;

  // ── Sub-section renderers ──
  const renderManualAllowance = () => {
    if (!v2) return renderV1ManualAllowance();

    return (
      <Box>
        {amountsRule}
        <Stack spacing="20px">
          <Typography>Choose how to set the allowance amount.</Typography>

          <CheckboxField
            formik={formik}
            type="radio"
            name="maxBenefit"
            options={inputOptions}
            displayKey="display"
            valueKey="value"
            disabled={displayOnly}
            showError={shouldShowErrors}
            validation={({ value }) => {
              const maxBenefit = value || {};
              const hasSel = Object.values(maxBenefit).some(Boolean);
              if (!hasSel) return "Please select a benefit type";
              return null;
            }}
          />

          <Field name="maxBenefit_required">
            {({ field }) => <input {...field} type="hidden" />}
          </Field>
          {errors.maxBenefit_required && shouldShowErrors && (
            <ErrorLine>{errors.maxBenefit_required}</ErrorLine>
          )}

          {/* TextField already reserves a helper-text line below its input
              (the " " fallback in its info slot), so it carries its own
              trailing space — no minHeight box and no Stack gap needed here. */}
          {(planType === "ICHRA"
            ? Object.values(values?.maxBenefit || {}).some(Boolean) &&
              !values.maxBenefit.FAMILY &&
              !values.maxBenefit.FAMILY_AGE &&
              !values.maxBenefit.AGE
            : values.maxBenefit?.SELF) && (
            <Box>
              <TextField
                formik={formik}
                type="number"
                name="monthlySelfAmount"
                label={selfLabel}
                disabled={displayOnly}
                validation="string"
              />
              {monthlyMaxMessage("monthlySelfAmount")}
              {learnMoreHref && <AccrualNote href={learnMoreHref} />}
            </Box>
          )}

          {/* AGE and FAMILY_AGE keep the note here, above the tables. SELF and
              FAMILY render their own copies next to their fields — FAMILY's
              fields are in a separate Stack after the tables, so a shared
              instance here would sit above them. */}
          {learnMoreHref &&
            (values?.maxBenefit?.AGE || values?.maxBenefit?.FAMILY_AGE) && (
              <AccrualNote href={learnMoreHref} />
            )}
        </Stack>

        {/* Age Allowance Table */}
        {values?.maxBenefit?.AGE && (
          <Fragment>
            <Box sx={{ marginTop: "24px" }}>
              <AgeAllowanceTable {...tableProps} />
            </Box>
            {ageTableMessage}
          </Fragment>
        )}

        {/* Family Age Allowance Table */}
        {values?.maxBenefit?.FAMILY_AGE && (
          <Fragment>
            <Box sx={{ marginTop: "24px" }}>
              <FamilyAllowanceTable {...tableProps} />
            </Box>
            {ageTableMessage}
          </Fragment>
        )}

        {/* Family amounts */}
        <Stack spacing={0} sx={{ marginTop: "20px" }}>
          {values?.maxBenefit?.FAMILY && (
            <Fragment>
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "1rem",
                  alignItems: "flex-end",
                }}
              >
                {planType === "QSEHRA" && (
                  <Typography sx={{ marginTop: "0px" }}>
                    Salusion caps the monthly allowance at the IRS family limit
                    ({monthlyFamilyDisplayMax} per month).
                  </Typography>
                )}

                <Stack sx={{ width: { lg: "48%", xl: "31%" } }}>
                  <TextField
                    formik={formik}
                    type="number"
                    name="monthlySelfAmount"
                    label="Monthly Self Amount"
                    disabled={displayOnly}
                    validation="string"
                  />
                  {monthlyMaxMessage("monthlySelfAmount")}
                </Stack>

                <Stack sx={{ width: { lg: "48%", xl: "31%" } }}>
                  <TextField
                    formik={formik}
                    type="number"
                    name="monthlyFirstDependentAmount"
                    label="First Dependent Amount (Spouse or Child)"
                    placeHolder="0"
                    disabled={displayOnly}
                  />
                  {monthlyMaxMessage("monthlyFirstDependentAmount")}
                </Stack>

                <Stack sx={{ width: { lg: "48%", xl: "31%" } }}>
                  <TextField
                    formik={formik}
                    type="number"
                    name="monthlyAdditionalDependentAmount"
                    label="Each Additional Dependent Amount"
                    placeHolder="0"
                    disabled={displayOnly}
                  />
                  {monthlyMaxMessage("monthlyAdditionalDependentAmount")}
                </Stack>
              </Stack>

              {learnMoreHref && <AccrualNote href={learnMoreHref} />}
            </Fragment>
          )}
        </Stack>
      </Box>
    );
  };

  const renderV1ManualAllowance = () => (
    <Box>
      {amountsRule}
      <Typography variant="h2" sx={employerFlowStyles.h2}>
        Benefit
      </Typography>
      <Typography>Enter the monthly allowance</Typography>

      <Stack spacing={2} sx={{ marginTop: "20px" }}>
        <CheckboxField
          formik={formik}
          type="radio"
          name="maxBenefit"
          options={inputOptions}
          displayKey="display"
          valueKey="value"
          disabled={displayOnly}
          showError={shouldShowErrors}
          validation={({ value }) => {
            const maxBenefit = value || {};
            const hasSel = Object.values(maxBenefit).some(Boolean);
            if (!hasSel) return "Please select a benefit type";
            return null;
          }}
        />
        <Field name="maxBenefit_required">
          {({ field }) => <input {...field} type="hidden" />}
        </Field>
        {errors.maxBenefit_required && shouldShowErrors && (
          <ErrorLine>{errors.maxBenefit_required}</ErrorLine>
        )}

        {values?.maxBenefit?.FAMILY && (
          <Fragment>
            <TextField
              formik={formik}
              type="number"
              name="monthlySelfAmount"
              label={selfLabel}
              disabled={displayOnly}
              validation="string"
            />
            {monthlyMaxMessage("monthlySelfAmount")}
          </Fragment>
        )}

        {values?.maxBenefit?.SELF && (
          <Fragment>
            <TextField
              formik={formik}
              type="number"
              name="selfAmount"
              label="Monthly Self Amount"
              disabled={displayOnly}
              validation="string"
            />
            <TextField
              formik={formik}
              type="number"
              name="spouseAmount"
              label="Self and Spouse Amount"
              disabled={displayOnly}
              validation="string"
            />
            <TextField
              formik={formik}
              type="number"
              name="childAmount"
              label="Self and Children Amount"
              disabled={displayOnly}
              validation="string"
            />
            <TextField
              formik={formik}
              type="number"
              name="allAmount"
              label="Self, Spouse and Children Amount"
              disabled={displayOnly}
              validation="string"
            />
          </Fragment>
        )}

        {(values?.maxBenefit?.AGE || values?.maxBenefit?.FAMILY_AGE) && (
          <Fragment>
            <AgeAllowanceTable {...tableProps} />
            {ageTableMessage}
          </Fragment>
        )}
      </Stack>
    </Box>
  );

  const renderMetalAllowance = () => {
    const familyChoiceFormik = {
      ...formik,
      values: {
        ...values,
        familyAllowanceChoice: familyAllowanceChoice
          ? { [familyAllowanceChoice]: true }
          : {},
      },
      setFieldValue: (name, value) => {
        if (name !== "familyAllowanceChoice") return;
        const choice = Object.keys(value || {}).find((key) => value[key]) || "";
        setFamilyAllowanceChoice(choice);
        if (choice === "NO") setFieldValue("metalPercentDependent", 0, true);
      },
    };
    const omitAcaSelection = getOmitAcaSelection(values);
    const omitAcaMinimum = omitAcaSelection === "true";
    const metalPercentInvalid =
      (touched.metalPercent && errors.metalPercent) ||
      (shouldShowErrors && (!values.metalPercent || values.metalPercent <= 0));
    const metalPercentBorder = metalPercentInvalid ? "#dc2626" : "#ACBEE8";

    return (
      <Box sx={{ marginTop: "20px" }}>
        <Stack spacing={2}>
          <Typography sx={{ fontWeight: 600 }}>Employee allowance</Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "flex-end" }}
          >
            <Stack flex={1}>
              <Typography sx={{ marginBottom: "8px" }}>Metal Level</Typography>
              <select
                value={values.metalLevel}
                onChange={(evt) =>
                  setValues({ ...values, metalLevel: evt.target.value })
                }
                disabled={displayOnly}
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `1px solid ${(touched.metalLevel && errors.metalLevel) || (shouldShowErrors && !values.metalLevel) ? "#dc2626" : "#ACBEE8"}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                <option value="">Select Metal Level</option>
                <option value="Bronze">Bronze</option>
                <option value="Silver">Silver</option>
                <option value="Gold">Gold</option>
              </select>
              {((touched.metalLevel && errors.metalLevel) ||
                (shouldShowErrors && !values.metalLevel)) && (
                <ErrorLine>
                  {errors.metalLevel || "Metal level is required"}
                </ErrorLine>
              )}
            </Stack>

            <Stack flex={1}>
              <Typography sx={{ marginBottom: "8px" }}>
                Average premium covered
              </Typography>
              <OutlinedInput
                type="number"
                value={values.metalPercent ?? ""}
                name="metalPercent"
                onChange={(evt) => {
                  const inputValue = evt.target.value;
                  const value =
                    inputValue === ""
                      ? undefined
                      : isNaN(Number(inputValue))
                        ? undefined
                        : Number(inputValue);
                  setFieldValue("metalPercent", value, true);
                }}
                onBlur={() => setFieldTouched("metalPercent", true)}
                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                disabled={displayOnly}
                sx={{
                  ...styles.outlinedInput,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: metalPercentBorder,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: metalPercentBorder,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: metalPercentBorder,
                  },
                }}
              />
              {metalPercentInvalid && (
                <ErrorLine>
                  {errors.metalPercent ||
                    (values.metalPercent === 0
                      ? "Percent of plan to cover must be greater than 0"
                      : "Percent of plan to cover is required")}
                </ErrorLine>
              )}
            </Stack>
          </Stack>

          <Fragment>
            {/* Legacy classes (metalV2 off) price dependents from their own rates */}
            {metalV2 && (
              <Box sx={{ borderTop: "1px solid #DCE3F3", paddingTop: "20px" }}>
                <Typography sx={{ fontWeight: 600, marginBottom: "4px" }}>
                  Create an allowance for spouse and dependents?
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#465578",
                    marginBottom: "12px",
                  }}
                >
                  An amount is calculated for each dependent. These allowances
                  are added to the total monthly allowance for the family.
                </Typography>
                <CheckboxField
                  formik={familyChoiceFormik}
                  type="radio"
                  name="familyAllowanceChoice"
                  options={[
                    { display: "Yes", value: "YES" },
                    { display: "No", value: "NO" },
                  ]}
                  displayKey="display"
                  valueKey="value"
                  disabled={displayOnly}
                />
                {shouldShowErrors && !familyAllowanceChoice && (
                  <ErrorLine>Please select Yes or No</ErrorLine>
                )}
                {familyAllowanceChoice === "YES" && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing="10px"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ marginTop: "16px" }}
                  >
                    <OutlinedInput
                      type="number"
                      value={values.metalPercentDependent ?? ""}
                      name="metalPercentDependent"
                      onChange={(evt) => {
                        const inputValue = evt.target.value;
                        setFieldValue(
                          "metalPercentDependent",
                          inputValue === "" ? undefined : Number(inputValue),
                          true,
                        );
                      }}
                      onBlur={() =>
                        setFieldTouched("metalPercentDependent", true)
                      }
                      endAdornment={
                        <InputAdornment position="end">%</InputAdornment>
                      }
                      disabled={displayOnly}
                      sx={{ ...styles.outlinedInput, width: { sm: "180px" } }}
                    />
                    <Typography sx={{ fontSize: "14px" }}>
                      % of the employee’s allowance per spouse or dependent.
                    </Typography>
                  </Stack>
                )}
                {familyAllowanceChoice === "YES" &&
                  shouldShowErrors &&
                  (!values.metalPercentDependent ||
                    Number(values.metalPercentDependent) <= 0) && (
                    <ErrorLine>
                      Percent for spouse / dependent must be greater than 0
                    </ErrorLine>
                  )}
                {familyAllowanceChoice === "YES" &&
                  touched.metalPercentDependent &&
                  errors.metalPercentDependent && (
                    <ErrorLine>{errors.metalPercentDependent}</ErrorLine>
                  )}
              </Box>
            )}

            <Box sx={{ borderTop: "1px solid #DCE3F3", paddingTop: "20px" }}>
              <Typography sx={{ fontWeight: 600, marginBottom: "4px" }}>
                Apply ACA affordability minimum?
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#465578" }}>
                Raises the employee allowance if needed to meet ACA
                affordability.
              </Typography>
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#465578",
                  marginBottom: "12px",
                }}
              >
                Most small employers aren’t subject to the ACA employer mandate.
              </Typography>
              {/* Stored as metalOmitAcaAffordability, so "Yes, apply" = "false" */}
              <CheckboxField
                formik={formik}
                type="radio"
                name="metalOmitAcaAffordability"
                options={[
                  { display: "Yes", value: "false" },
                  { display: "No", value: "true" },
                ]}
                displayKey="display"
                valueKey="value"
                disabled={displayOnly}
              />
              {shouldShowErrors && omitAcaSelection === undefined && (
                <ErrorLine>Please select Yes or No</ErrorLine>
              )}
            </Box>

            <Stack spacing="12px" sx={{ paddingTop: "4px" }}>
              {calculationMethodOptions[0].links.map((link, linkIndex) => (
                <LearnMoreButton
                  key={linkIndex}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.text}
                </LearnMoreButton>
              ))}
            </Stack>
          </Fragment>
        </Stack>
      </Box>
    );
  };

  const renderACAAllowance = () => null;

  // ── Main render ──
  return (
    <Stack spacing="30px" height="100%">
      <FormContainer>
        <Typography variant="h2" sx={employerFlowStyles.h2}>
          Monthly Allowance
        </Typography>
        <Typography>How do you want to set your allowance?</Typography>

        <Stack spacing="16px" sx={{ marginTop: "20px", marginBottom: "30px" }}>
          {topLevelOptions.map((option) => (
            <Box key={option.value}>
              <CheckboxField
                formik={topLevelFormik}
                type="radio"
                name="topLevelChoice"
                options={[option]}
                displayKey="display"
                valueKey="value"
                disabled={displayOnly}
                showError={false}
              />
            </Box>
          ))}
        </Stack>

        <Stack>
          {/* Sub-options when "Salusion Calculates" is selected */}
          {topLevelChoice === "CALCULATED" && (
            <Box>
              <Typography sx={{ marginBottom: "20px" }}>
                Select the calculation method.
              </Typography>
              <Stack spacing="16px">
                {calculationMethodOptions.map((method) => (
                  <Box key={method.value}>
                    <CheckboxField
                      formik={formik}
                      type="radio"
                      name="allowanceMethod"
                      options={[method]}
                      displayKey="display"
                      valueKey="value"
                      disabled={displayOnly}
                      showError={false}
                    />
                    <Box
                      component="ul"
                      sx={{ margin: "4px 0 0 28px", paddingLeft: "20px" }}
                    >
                      {method.bullets.map((bullet, index) => (
                        <Typography
                          component="li"
                          key={index}
                          sx={{
                            fontSize: "14px",
                            color: "#465578",
                            marginBottom: "2px",
                          }}
                        >
                          {bullet}
                        </Typography>
                      ))}
                      {method.value === "ACA_MINIMUM" &&
                        values?.acaSalary > 0 && (
                          <Typography
                            component="li"
                            sx={{
                              fontSize: "14px",
                              color: "#465578",
                              marginBottom: "2px",
                            }}
                          >
                            Based on minimum salary of{" "}
                            {getCurrency({
                              amount: values.acaSalary,
                              actions: ["divide"],
                            })}
                          </Typography>
                        )}
                    </Box>
                  </Box>
                ))}
              </Stack>

              {/* Links shown based on selected calculation method. PCT_OF_METAL
                  shows its links inside renderMetalAllowance() instead. */}
              {activeMethod &&
                activeMethod !== "PCT_OF_METAL" &&
                calculationMethodOptions
                  .filter((m) => m.value === activeMethod)
                  .map((method) => (
                    <Stack
                      key={method.value}
                      spacing="12px"
                      sx={{ marginTop: "20px" }}
                    >
                      {method.links.map((link, linkIndex) => (
                        <LearnMoreButton
                          key={linkIndex}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {link.text}
                        </LearnMoreButton>
                      ))}
                    </Stack>
                  ))}
            </Box>
          )}

          {showSelectionError && (
            <ErrorLine>
              {topLevelChoice === "CALCULATED"
                ? "Please select a calculation method before continuing"
                : "Please select an allowance method before continuing"}
            </ErrorLine>
          )}

          <Field name="allowanceMethod_required">
            {({ field }) => <input {...field} type="hidden" />}
          </Field>

          {/* ── Conditional detail sections ── */}
          {activeMethod === "MANUAL" && renderManualAllowance()}
          {activeMethod === "PCT_OF_METAL" && renderMetalAllowance()}
          {activeMethod === "ACA_MINIMUM" && renderACAAllowance()}
        </Stack>
      </FormContainer>

      <NavButtons
        mode={mode}
        enableSave={true}
        exitUrl={exitUrl}
        step={step}
        onNextStep={handleNext}
      />
    </Stack>
  );
}
